import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Button } from 'react-scale/components/design-system/Button';
import { Modal, ModalBody, ModalFooter } from 'design-system/Modal';
import { Form, InputText } from 'design-system/Form';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { IIMetricsData } from './types';
import { IWorkerIdToEmailMap } from '@scale/shared/types/corp/quality';
import { useAsyncFn, useUpdateEffect } from 'react-use-v13';
import SAlert from 'react-s-alert';

const ReviewerExclusionModal = ({
  batchIdToUpdate,
  instrItemThresholds,
  manuallyExcludedReviewers,
  addingExcludedReviewer,
  removingExcludedReviewer,
  minimumBenchmarksPerII,
  workerIdToEmailMap,
  metricsData,
  countReviewerValidAttempts,
  isOpen,
  onClose,
  onUpdateAuditBatch,
  prepopulatedReviewerId = '',
}: {
  batchIdToUpdate?: string;
  instrItemThresholds: { [key: string]: number[] };
  manuallyExcludedReviewers: string[];
  addingExcludedReviewer: boolean;
  removingExcludedReviewer: string;
  minimumBenchmarksPerII: { [key: string]: number };
  workerIdToEmailMap: IWorkerIdToEmailMap;
  metricsData: IIMetricsData;
  countReviewerValidAttempts: (reviewerID: string, metrics: IIMetricsData) => number;
  isOpen: boolean;
  onClose: () => void;
  onUpdateAuditBatch?: (auditBatch: any, attemptIds: string[]) => Promise<void>;
  prepopulatedReviewerId?: string;
}) => {
  const [reviewerExclusionText, setReviewerExclusionText] = useState('');
  const [allowReviewerExclusion, setAllowReviewerExclusion] = useState<boolean>(false);
  const [newExcludedReviewer, setNewExcludedReviewer] = useState<string>('');

  const [updatedAuditBatch, editAuditBatch] = useAsyncFn(async () => {
    const reviewerID = reviewerEmailToID(newExcludedReviewer);
    if (
      addingExcludedReviewer &&
      newExcludedReviewer &&
      !manuallyExcludedReviewers.includes(reviewerID)
    ) {
      manuallyExcludedReviewers.push(reviewerID);
    } else if (removingExcludedReviewer) {
      _.remove(manuallyExcludedReviewers, reviewer => reviewer === removingExcludedReviewer);
    } else {
      return;
    }

    const { auditBatch, attemptIds } = await apiFetch<{
      auditBatch: any;
      attemptIds: string[];
    }>(null, `corp-api/quality/audits/${batchIdToUpdate}`, {
      method: 'PUT',
      body: {
        instrItemThresholds,
        minimumBenchmarksPerII,
        manuallyExcludedReviewers,
      },
      maxRetries: 1,
    });
    onUpdateAuditBatch?.(auditBatch, attemptIds);
    onClose();
    return { auditBatch, attemptIds };
  }, [
    addingExcludedReviewer,
    newExcludedReviewer,
    removingExcludedReviewer,
    instrItemThresholds,
    minimumBenchmarksPerII,
    manuallyExcludedReviewers,
  ]);

  // Error handling
  useUpdateEffect(() => {
    if (updatedAuditBatch.error) {
      SAlert.error(`Error updating audit batch: ${updatedAuditBatch.error.message}`);
    }
  }, [updatedAuditBatch.error]);

  // If you passed in a user email, find the ID for that user email
  const reviewerEmailToID = (reviewerIdOrEmail: string) => {
    const reviewerObjFromEmail = _.find(
      workerIdToEmailMap,
      userObj => userObj.email === reviewerIdOrEmail.trim(),
    );
    return reviewerObjFromEmail?._id?.toString() || reviewerIdOrEmail;
  };

  useEffect(
    // Validates the currently-entered reviewer and sets the modal display text
    // If the reviewer is valid, we enable the modal button
    () => {
      const reviewerID = reviewerEmailToID(newExcludedReviewer);

      if (!newExcludedReviewer) {
        setReviewerExclusionText('');
        setAllowReviewerExclusion(false);
        return;
      }

      if (_.includes(manuallyExcludedReviewers, reviewerID)) {
        setReviewerExclusionText('Reviewer has already been manually excluded from this batch');
        setAllowReviewerExclusion(false);
        return;
      }

      const reviewerMetrics = metricsData.filteredMetricsByWorker[reviewerID];

      if (!reviewerMetrics) {
        setReviewerExclusionText('Reviewer not found in this batch');
        setAllowReviewerExclusion(false);
        return;
      }

      const passingThreshold = _.every(
        instrItemThresholds,
        (threshold, ii) =>
          reviewerMetrics[ii] >= (threshold[0] || 0) && reviewerMetrics[ii] <= (threshold[1] || 1),
      );

      // Validate if is a worker that has valid tasks (i.e. not already filtered by ii thresholds)
      if (!passingThreshold) {
        setReviewerExclusionText(
          'Worker is already filtered out by current instruction item thresholds',
        );
        setAllowReviewerExclusion(false);
        return;
      }

      const numValidAttempts = countReviewerValidAttempts(reviewerID, metricsData);
      setAllowReviewerExclusion(true);
      setReviewerExclusionText(
        `Excluding this reviewer will remove ${numValidAttempts} tasks from the current batch`,
      );
    }, // Only want to watch changes to newExcludedReviewer
    [newExcludedReviewer],
  );

  useEffect(() => setNewExcludedReviewer(prepopulatedReviewerId), [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Reviewer Exclusion Modal"
      onRequestClose={() => {
        onClose();
      }}
      maxWidth="500px"
      zIndex={1000}
    >
      <ModalBody>
        <Form mode="vertical">
          {addingExcludedReviewer && (
            <>
              <h2>Manually Exclude Reviewer</h2>
              <InputText
                defaultValue={prepopulatedReviewerId}
                onChange={event => setNewExcludedReviewer(event.target.value)}
              />
              <p>{reviewerExclusionText}</p>
            </>
          )}
          {!!removingExcludedReviewer.length && (
            <>
              <h2>Undo Reviewer Exclusion</h2>
              <p>
                Remove{' '}
                <strong>
                  {workerIdToEmailMap[removingExcludedReviewer]?.email || removingExcludedReviewer}
                </strong>{' '}
                from manually-excluded reviewers list?
              </p>
            </>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          type="primary"
          onClick={editAuditBatch}
          disabled={
            updatedAuditBatch.loading || (addingExcludedReviewer && !allowReviewerExclusion)
          }
        >
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReviewerExclusionModal;
